/**
 * Stockage first-party de l'identite du visiteur.
 *
 * C'est LE point d'architecture du widget. Le chat est rendu dans une iframe
 * servie par notre domaine, donc un contexte tiers pour le site hote : Safari/ITP
 * cloisonne le localStorage d'une iframe tierce et Chrome supprime les cookies
 * tiers. Un visitorToken conserve dans l'iframe serait donc perdu, et la
 * reconnaissance des visiteurs de retour (Phase 8) ne fonctionnerait pas.
 *
 * Le jeton est par consequent conserve ICI, dans la page hote, donc en
 * first-party -- et transmis a l'iframe par l'URL puis par postMessage.
 *
 * Trois niveaux de repli, parce qu'un stockage refuse ne doit jamais casser le
 * chat : le visiteur en navigation privee doit pouvoir discuter, meme s'il n'est
 * pas reconnu a sa prochaine visite.
 */

export type StoreKind = 'local' | 'cookie' | 'memory';

export interface WidgetStore {
  readonly kind: StoreKind;
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

const COOKIE_MAX_AGE_SECONDS = 400 * 24 * 60 * 60; // Plafond impose par Chrome.

/** Les cles sont prefixees par la widgetKey : deux sites cohabitent sans se marcher dessus. */
function sanitize(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 64);
}

function tryLocalStorage(probeKey: string): Storage | null {
  try {
    // Safari en navigation privee accepte l'acces a l'objet mais leve a
    // l'ecriture : la sonde doit ecrire pour etre concluante.
    const storage = window.localStorage;
    storage.setItem(probeKey, '1');
    storage.removeItem(probeKey);
    return storage;
  } catch {
    return null;
  }
}

function readCookie(name: string): string | null {
  const encoded = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(encoded)) {
      try {
        return decodeURIComponent(entry.slice(encoded.length));
      } catch {
        return null;
      }
    }
  }
  return null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${encodeURIComponent(name)}=${encodeURIComponent(value)}` +
    `; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function tryCookies(probeKey: string): boolean {
  try {
    writeCookie(probeKey, '1', 60);
    const works = readCookie(probeKey) === '1';
    writeCookie(probeKey, '', 0);
    return works;
  } catch {
    return false;
  }
}

export function createStore(namespace: string): WidgetStore {
  const prefix = `chatwdg.${sanitize(namespace)}.`;
  const probeKey = `${prefix}probe`;

  const local = tryLocalStorage(probeKey);
  if (local) {
    return {
      kind: 'local',
      get(key) {
        try {
          return local.getItem(prefix + key);
        } catch {
          return null;
        }
      },
      set(key, value) {
        try {
          local.setItem(prefix + key, value);
        } catch {
          // Quota depasse ou stockage revoque en cours de session : on continue
          // sans persistance plutot que de faire remonter une exception dans la
          // page du client.
        }
      },
      remove(key) {
        try {
          local.removeItem(prefix + key);
        } catch {
          /* idem */
        }
      },
    };
  }

  if (tryCookies(probeKey)) {
    return {
      kind: 'cookie',
      get: (key) => readCookie(prefix + key),
      set: (key, value) => writeCookie(prefix + key, value, COOKIE_MAX_AGE_SECONDS),
      remove: (key) => writeCookie(prefix + key, '', 0),
    };
  }

  const memory = new Map<string, string>();
  return {
    kind: 'memory',
    get: (key) => memory.get(key) ?? null,
    set: (key, value) => void memory.set(key, value),
    remove: (key) => void memory.delete(key),
  };
}

/** Cles utilisees. Centralisees pour eviter les fautes de frappe silencieuses. */
export const STORE_KEYS = {
  visitorToken: 'token',
  consent: 'consent',
  session: 'session',
  teaserDismissed: 'teaser',
} as const;

/** Lecture JSON tolerante : une valeur corrompue est ignoree, pas une exception. */
export function readJson<T>(store: WidgetStore, key: string, isValid: (value: unknown) => T | null): T | null {
  const raw = store.get(key);
  if (!raw) return null;
  try {
    return isValid(JSON.parse(raw) as unknown);
  } catch {
    store.remove(key);
    return null;
  }
}

export function writeJson(store: WidgetStore, key: string, value: unknown): void {
  try {
    store.set(key, JSON.stringify(value));
  } catch {
    /* Valeur non serialisable : ne peut pas arriver avec nos structures. */
  }
}
